'use client'

import { createContext, useCallback, useContext, useState } from 'react'
import type { ProductPageType } from '@productsite-beta/src/components/product-page-v2'

/**
 * Optimistic state for the product page during variant transitions.
 * Each field is null when the server-rendered value should be used.
 * When set, the optimistic value takes precedence until the server reconciles.
 */
export type ProductOptimisticState = {
  primaryImageId: string | null
  price: number | null
  displayName: string | null
  specs: string | null
  availability: ProductPageType['availability'] | null
  campaign: { isCampaign: boolean; originalPrice: number } | null
  isNavigating: boolean
}

type ProductOptimisticContextValue = ProductOptimisticState & {
  setOptimistic: (updates: Partial<ProductOptimisticState>) => void
  resetOptimistic: () => void
}

const INITIAL_STATE: ProductOptimisticState = {
  primaryImageId: null,
  price: null,
  displayName: null,
  specs: null,
  availability: null,
  campaign: null,
  isNavigating: false,
}

const ProductOptimisticContext = createContext<ProductOptimisticContextValue>({
  ...INITIAL_STATE,
  setOptimistic: () => {},
  resetOptimistic: () => {},
})

export function ProductOptimisticProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ProductOptimisticState>(INITIAL_STATE)

  const setOptimistic = useCallback((updates: Partial<ProductOptimisticState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const resetOptimistic = useCallback(() => {
    setState(INITIAL_STATE)
  }, [])

  return (
    <ProductOptimisticContext.Provider value={{ ...state, setOptimistic, resetOptimistic }}>
      {children}
    </ProductOptimisticContext.Provider>
  )
}

// Granular hooks — consumers only re-render when their slice changes
export function useOptimisticImage() {
  const { primaryImageId, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticImageId = useCallback(
    (id: string | null) => setOptimistic({ primaryImageId: id }),
    [setOptimistic]
  )
  return { optimisticImageId: primaryImageId, setOptimisticImageId }
}

export function useOptimisticPrice() {
  const { price, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticPrice = useCallback(
    (p: number | null) => setOptimistic({ price: p }),
    [setOptimistic]
  )
  return { optimisticPrice: price, setOptimisticPrice }
}

export function useOptimisticTitle() {
  const { displayName, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticTitle = useCallback(
    (name: string | null) => setOptimistic({ displayName: name }),
    [setOptimistic]
  )
  return { optimisticTitle: displayName, setOptimisticTitle }
}

export function useOptimisticSpecs() {
  const { specs, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticSpecs = useCallback(
    (s: string | null) => setOptimistic({ specs: s }),
    [setOptimistic]
  )
  return { optimisticSpecs: specs, setOptimisticSpecs }
}

export function useOptimisticAvailability() {
  const { availability, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticAvailability = useCallback(
    (a: ProductOptimisticState['availability']) => setOptimistic({ availability: a }),
    [setOptimistic]
  )
  return { optimisticAvailability: availability, setOptimisticAvailability }
}

export function useOptimisticCampaign() {
  const { campaign, setOptimistic } = useContext(ProductOptimisticContext)
  const setOptimisticCampaign = useCallback(
    (c: ProductOptimisticState['campaign']) => setOptimistic({ campaign: c }),
    [setOptimistic]
  )
  return { optimisticCampaign: campaign, setOptimisticCampaign }
}

/** Batch setter for dispatching multiple fields at once (e.g. on variant click) */
export function useProductOptimistic() {
  return useContext(ProductOptimisticContext)
}

export function useIsNavigating() {
  const { isNavigating } = useContext(ProductOptimisticContext)
  return isNavigating
}
